import { For, Show, type JSX, type ParentComponent } from 'solid-js';

/**
 * The persistent left rail (SPEC-V2.md §4.1) — not a modal.
 *
 * Two tabs over one space: **Spreads**, the catalogue of parts, and
 * **Strategies**, the stacks built from them. Spreads leads because it is the
 * only tab with anything in it on a first visit, and because you cannot stack
 * something you have not got.
 *
 * The rail owns nothing but the frame. What goes in a tab arrives as children,
 * so the catalogue's editor wiring and the strategy list's composer wiring both
 * stay in `App` rather than leaking in here.
 *
 * Export and import sit above both tabs: they cover the whole database, so
 * hiding either behind a tab would make a data-safety affordance conditional on
 * where you happened to be looking.
 */

export type RailTab = 'spreads' | 'strategies';

export interface LibraryRailProps {
  activeTab: RailTab;
  onTabChange: (tab: RailTab) => void;
  /** Shown on the Strategies tab so the rail can be read without opening it. */
  savedCount: number;
  onExport: () => void;
  onImport: (file: File) => void;
  class?: string;
  children?: JSX.Element;
}

const TABS: readonly (readonly [RailTab, string])[] = [
  ['spreads', 'Spreads'],
  ['strategies', 'Strategies'],
];

const LibraryRail: ParentComponent<LibraryRailProps> = (props) => {
  let fileInput: HTMLInputElement | undefined;

  return (
    <div class={`flex h-full flex-col ${props.class ?? ''}`}>
      <header class="shrink-0 space-y-2 border-b border-line p-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-[13px] font-medium text-ink">Library</h2>
          <div class="flex gap-1">
            <button
              type="button"
              onClick={props.onExport}
              class="rounded border border-line bg-surface-2 px-2 py-1 text-[10px] text-ink-dim hover:text-ink"
            >
              Export
            </button>
            <button
              type="button"
              onClick={() => fileInput?.click()}
              class="rounded border border-line bg-surface-2 px-2 py-1 text-[10px] text-ink-dim hover:text-ink"
            >
              Import
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="application/json"
              class="hidden"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) props.onImport(file);
                // Reset so choosing the same file again still fires a change.
                event.currentTarget.value = '';
              }}
            />
          </div>
        </div>

        <div class="flex overflow-hidden rounded border border-line text-[11px]">
          <For each={TABS}>
            {([value, label]) => (
              <button
                type="button"
                onClick={() => props.onTabChange(value)}
                class="flex-1 px-2 py-1 transition-colors"
                classList={{
                  'bg-placed/25 text-ink': props.activeTab === value,
                  'bg-surface-2 text-ink-dim hover:text-ink': props.activeTab !== value,
                }}
              >
                {label}
                <Show when={value === 'strategies' && props.savedCount > 0}>
                  <span class="tnum text-ink-faint"> · {props.savedCount}</span>
                </Show>
              </button>
            )}
          </For>
        </div>
      </header>

      {/* The list scrolls internally — the rail itself stays put. */}
      <div class="min-h-0 flex-1 overflow-y-auto p-3">{props.children}</div>
    </div>
  );
};

export default LibraryRail;
